<template>
    <DashboardCard
        v-if="loading || showError || scorecards?.length > 0"
        :title="formatScorecardName(selectedScorecard)"
        :icon="faChartColumn"
        :response-count="selectedScorecard?.responseCount ?? 0"
        :aria-busy="loading"
        :show-error="showError"
        :on-reload-click="onReloadClick"
    >
        <template v-if="!loading && scorecards?.length > 1" #header>
            <Button
                id="select-scorecard-popover-button"
                variant="text"
                icon="pi pi-chevron-down"
                aria-label="Select Scorecard"
                size="large"
                aria-haspopup="menu"
                aria-controls="scorecard-popover"
                @click="togglePopover"
            />
            <Popover
                id="scorecard-popover"
                ref="popover"
                :dt="{ contentPadding: 0 }"
                @show="focusOnMenu"
            >
                <ul
                    id="select-scorecard-list"
                    ref="scorecardList"
                    role="menu"
                    aria-label="Scorecard list"
                >
                    <li
                        v-for="scorecard in scorecards"
                        :key="scorecard.id"
                        class="select-scorecard-list-item"
                        role="menuitem"
                    >
                        <Button
                            class="select-new-scorecard-button"
                            :label="formatScorecardName(scorecard)"
                            :icon="
                                scorecard.id === selectedScorecard?.id
                                    ? 'pi pi-check'
                                    : undefined
                            "
                            variant="text"
                            severity="secondary"
                            icon-pos="right"
                            icon-size="large"
                            @click="selectNewScorecard(scorecard.id)"
                        />
                    </li>
                </ul>
            </Popover>
        </template>

        <template #content>
            <dl
                v-if="!loading && scorecards.length"
                class="scorecard-topics"
                aria-label="Scorecard topics"
            >
                <div
                    v-for="topic in selectedScorecard.topics"
                    :key="topic.id"
                    class="topic"
                >
                    <dt :id="`topic--name-${topic.id}`" class="topic--name">
                        {{ topic.name }}
                    </dt>

                    <dd
                        class="topic--score"
                        :aria-describedby="`aria-score-desc-${topic.id}`"
                    >
                        <span
                            :id="`aria-score-desc-${topic.id}`"
                            class="screen-reader-only"
                        >
                            {{
                                getAriaScoreDesc(
                                    topic.selectedPeriodScore,
                                    topic.changeSinceLastPeriod,
                                    timePeriod,
                                    metric
                                )
                            }}
                        </span>

                        <div
                            class="topic--score--current-score"
                            aria-hidden="true"
                        >
                            {{
                                formatScoreWithPercentage(
                                    topic.selectedPeriodScore,
                                    isFiveScore,
                                    csatAsPercent,
                                    metric,
                                    decimals
                                )
                            }}
                        </div>
                        <FontAwesomeIcon
                            :icon="getDiffIcon(topic.changeSinceLastPeriod)"
                            :class="[
                                'topic--score--icon',
                                `topic--score--icon__${getDiffVariant(topic.changeSinceLastPeriod)}`,
                            ]"
                            aria-hidden="true"
                        />
                        <div
                            :class="[
                                'topic--score--prev-score',
                                `topic--score--prev-score__${getDiffVariant(topic.changeSinceLastPeriod)}`,
                            ]"
                            aria-hidden="true"
                        >
                            {{
                                formatScoreWithPercentage(
                                    topic.changeSinceLastPeriod,
                                    isFiveScore,
                                    csatAsPercent,
                                    metric,
                                    decimals
                                )
                            }}
                        </div>
                    </dd>
                </div>
            </dl>

            <div v-else class="loading-topics">
                <span class="screen-reader-only">Loading scorecard...</span>
                <div
                    v-for="column in 2"
                    :key="column"
                    class="loading-topics--column"
                >
                    <div
                        v-for="row in 3"
                        :key="row"
                        class="loading-topics--column--row"
                    >
                        <Skeleton
                            data-testid="loading-skeleton"
                            width="50%"
                            height="18px"
                            border-radius="100px"
                        />
                        <Skeleton
                            data-testid="loading-skeleton"
                            width="25%"
                            height="18px"
                            border-radius="100px"
                        />
                    </div>
                </div>
            </div>
        </template>
    </DashboardCard>
</template>

<script lang="ts" setup>
import { ref, type Ref, computed, type ComputedRef, nextTick } from 'vue'
import { useStore } from 'vuex'
import Button from 'primevue/button'
import Skeleton from 'primevue/skeleton'
import Popover from 'primevue/popover'
import { FontAwesomeIcon } from 'fontawesome/vue-fontawesome'
import { faChartColumn } from 'fontawesome/pro-regular-svg-icons'
import {
    getDiffVariant,
    getDiffIcon,
    getAriaScoreDesc,
} from '@/utils/frontlineManagerDash'
import { formatScoreWithPercentage } from '@/utils/number'
import DashboardCard from '@/components/FrontlineManagerDash/DashboardCard.vue'

interface Props {
    scorecards: Scorecard[]
    loading: boolean
    metric: string
    timePeriod: string
    csatAsPercent: boolean
    isFiveScore: boolean
    decimals: {
        nps: number
        csat: number
        fivestar: number
        ces: number
    }
    showError?: boolean
    onReloadClick?: Function
}
const props = defineProps<Props>()

const store = useStore()

const popover = ref()
const scorecardList: Ref<HTMLElement | undefined> = ref()
const formatScorecardName = (scorecard: Scorecard) => {
    let scorecardName = scorecard?.name ?? ''

    if (!props.loading && scorecardName) {
        if (scorecardName.toLowerCase().endsWith(' scorecard')) {
            return scorecardName
        }

        if (scorecard?.isScorecard) {
            return `${scorecardName} Scorecard`
        }

        return scorecardName
    }
    return 'Scorecard'
}

const selectedScorecardId: ComputedRef<number> = computed(
    () => store.getters['frontlineManagerDash/selectedScorecardId'] ?? 0
)
const selectedScorecard: ComputedRef<Scorecard | undefined> = computed(() => {
    if (!props.scorecards?.length) return undefined
    return (
        props.scorecards.find((s) => s.id === selectedScorecardId.value) ??
        props.scorecards?.[0]
    )
})

const selectNewScorecard = (scorecardId: number) => {
    store.commit('frontlineManagerDash/setSelectedScorecardId', scorecardId)
    popover.value.hide()
}

const togglePopover = (event) => {
    popover.value.toggle(event)
}

const focusOnMenu = () => {
    nextTick(() => {
        scorecardList.value?.querySelector('button')?.focus()
    })
}
</script>

<style lang="less">
@import '~@/styles/rain/variables.less';

.select-new-scorecard-button {
    .p-button-label {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-weight: @fontWeight-medium;
    }
}
</style>

<style lang="less" scoped>
@import '~@/styles/prime-vue/variables.less';
@import '~@/styles/rain/variables.less';

#select-scorecard-popover-button {
    padding: @spacing-xxs @spacing-xs;
    width: fit-content;
}

#select-scorecard-list {
    display: flex;
    flex-direction: column;
    padding: 0;
    margin: 0;
    list-style-type: none;

    .select-scorecard-list-item {
        border-bottom: @borderWidth solid var(--p-gray-200);
    }
    .select-scorecard-list-item:last-child {
        border-bottom: unset;
    }
}

.select-new-scorecard-button {
    justify-content: space-between;
    gap: @spacing-lg;
    width: 200px;
    margin: @spacing-sm;
    font-size: @fontSize-sm;

    .p-button-label {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}

.scorecard-topics {
    display: grid;
    grid-template-columns: repeat(1, minmax(0, 1fr));
    grid-column-gap: @spacing-3xl;
    grid-row-gap: @spacing-xl;
    margin: @spacing-2xl 0 0;
}

.loading-topics {
    display: flex;
    gap: 20px;
    margin-top: @spacing-xl;

    &--column {
        display: flex;
        flex-direction: column;
        width: 100%;
        gap: 30px;

        &--row {
            display: flex;
            gap: 25%;
            justify-content: space-between;
        }
    }
}

.topic {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: @spacing-xl;
    color: var(--p-gray-600);
    margin: 0;

    &--name {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        font-size: @fontSize-md;
        line-height: @display-xs;
        font-weight: @fontWeight-medium;
    }
    &--score {
        display: flex;
        flex: 0 0 auto;
        align-items: end;
        gap: @spacing-sm;
        font-weight: @fontWeight-medium;

        &--current-score,
        &--icon {
            font-size: @fontSize-md;
            line-height: @fontSize-md;
            letter-spacing: @letterSpacing-sm;
        }

        &--icon {
            width: 14px;

            &__positive {
                color: var(--p-success-500);
            }
            &__neutral {
                color: var(--p-gray-400);
            }
            &__negative {
                color: var(--p-error-500);
            }
        }

        &--prev-score {
            font-size: @fontSize-sm;
            line-height: @fontSize-sm;
            letter-spacing: @letterSpacing-xs;

            &__positive {
                color: var(--p-success-600);
            }
            &__neutral {
                color: var(--p-gray-500);
            }
            &__negative {
                color: var(--p-error-600);
            }
        }
    }
}

@container dashboardCard (max-width: 300px) {
    .scorecard-topics {
        display: flex;
        flex-direction: column;
        gap: @spacing-xl;
    }
}
</style>
